import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const SOCType3_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            SOC Type 3 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            System and Organization Controls Type 3 — Publicly available report
            demonstrating security, availability, confidentiality, processing
            integrity, and privacy controls.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              SOC Type 3 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              SOC Type 3 (System and Organization Controls Type 3) is a publicly
              available audit report that provides a comprehensive overview of a
              service organization’s control environment related to security,
              availability, confidentiality, processing integrity, and privacy.
              Unlike SOC Type 1 and SOC Type 2, which are generally shared only
              with clients and stakeholders under non-disclosure agreements
              (NDAs), the SOC Type 3 report is designed to be made publicly
              available, providing transparency and assurance to a broader
              audience about an organization’s commitment to security and data
              protection.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              SOC Type 3 certification is typically used by service
              organizations that want to demonstrate their security practices
              and risk management controls in a transparent manner, often as
              part of their marketing efforts or public disclosures. It is
              particularly valuable for organizations in industries that
              prioritize customer trust, such as cloud service providers, IT
              outsourcing, and financial services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is SOC Type 3 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              SOC Type 3 is an audit report that assesses the effectiveness of
              an organization’s security controls and practices over a specified
              period, focusing on a range of Trust Services Criteria, including
              security, availability, confidentiality, processing integrity, and
              privacy. The SOC Type 3 report is similar to SOC Type 2, but the
              key difference is that it is intended for public sharing.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              Key features of SOC Type 3 include:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Public Availability: The SOC Type 3 report is intended to be shared publicly, allowing service organizations to provide transparency about their security practices to a wider audience, including potential clients, partners, and regulatory bodies.",
                "Third-Party Validation: The report is conducted by an independent third-party auditor, providing an unbiased and credible assessment of the organization’s control environment.",
                "Comprehensive Coverage: SOC Type 3 reports assess the organization’s performance against Trust Services Criteria, ensuring that the organization has implemented adequate security measures and that those measures are operating effectively over the audit period.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed mt-6">
              A SOC Type 3 report is particularly useful for organizations that
              want to demonstrate their commitment to security and data
              protection to a broader audience, showing that they have been
              independently audited and meet recognized industry standards.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of SOC Type 3 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Globe,
                title: "Publicly Available Report",
                desc: "Unlike SOC Type 1 and SOC Type 2, which are typically shared with individual clients or stakeholders, SOC Type 3 is designed for public distribution. This allows the organization to demonstrate its commitment to security and data protection to a larger audience, including potential customers and business partners.",
              },
              {
                icon: Target,
                title: "Focus on Trust Services Criteria",
                desc: "The SOC Type 3 audit evaluates an organization’s controls and practices against the Trust Services Criteria (TSC), which includes Security (ensuring that the organization’s systems are protected against unauthorized access and attacks), Availability (ensuring the systems are operational and available for use when needed), Confidentiality (protecting sensitive information from unauthorized access and ensuring data privacy), Processing Integrity (ensuring that system processes are complete, accurate, and reliable), and Privacy (complying with data privacy laws and safeguarding personal information).",
              },
              {
                icon: Award,
                title: "Independent Third-Party Audit",
                desc: "SOC Type 3 reports are conducted by an independent third-party auditor who assesses the organization’s controls and practices against the Trust Services Criteria. This third-party validation adds credibility and transparency to the report, building trust with clients and stakeholders.",
              },
              {
                icon: TrendingUp,
                title: "Demonstration of Operational Effectiveness",
                desc: "SOC Type 3 provides evidence that the organization’s security controls and processes have been operating effectively over time. It provides a detailed account of how the organization has met its security and privacy obligations, as well as its ability to manage risks and maintain compliance.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why SOC Type 3 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Transparency and Trust — The SOC Type 3 report helps service organizations demonstrate their commitment to security and data protection. By making the report publicly available, organizations can provide transparency and build trust with a broader range of stakeholders, including potential customers, partners, and regulatory bodies.",
                "Customer Assurance — SOC Type 3 certification provides assurance to clients that the service organization’s security controls and privacy practices have been independently validated and are functioning effectively over time. It helps clients make informed decisions about partnering with the organization.",
                "Marketing and Competitive Advantage — For service organizations that wish to differentiate themselves in a competitive market, SOC Type 3 certification is a valuable tool for demonstrating security and trustworthiness. It helps organizations show potential clients that they are committed to the highest standards of security and data privacy.",
                "Compliance with Industry Regulations — SOC Type 3 reports can help organizations meet regulatory and compliance requirements related to data security, privacy, and risk management. The report serves as evidence of the organization’s adherence to recognized industry standards and best practices.",
                "Enhanced Vendor and Partner Relationships — Sharing a SOC Type 3 report with vendors and partners can strengthen relationships and improve collaboration by demonstrating a shared commitment to security and data protection. It provides reassurance that your organization is capable of managing third-party risks and protecting sensitive information.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does SOC Type 3 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              SOC Type 3 certification includes a third-party audit report that
              evaluates the effectiveness of the organization’s security
              controls and their performance over a defined period. Key
              components of the SOC Type 3 report include:
            </p>
            <div className="space-y-4">
              {[
                "1. Control Environment Overview — The report provides an overview of the organization’s control environment, describing the security measures and processes in place to ensure that systems and data are secure and meet the Trust Services Criteria.",
                "2. Performance Against Trust Services Criteria — The report assesses the organization’s performance against the five Trust Services Criteria (security, availability, confidentiality, processing integrity, and privacy). It evaluates how well the organization has implemented and maintained its security controls over the reporting period.",
                "3. Independent Third-Party Audit — The audit is conducted by an independent, accredited third-party auditor, who assesses the organization’s controls and provides an unbiased evaluation of their effectiveness.",
                "4. Public Availability — Unlike SOC Type 1 and SOC Type 2 reports, SOC Type 3 reports are intended to be publicly shared, providing transparent evidence of the organization’s security posture and risk management practices.",
                "5. Detailed Findings and Recommendations — The report includes detailed findings from the audit, including any gaps or areas for improvement in the organization’s security controls. This information helps organizations enhance their security practices and address any vulnerabilities or weaknesses identified during the audit.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of SOC Type 3 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Enhanced Transparency and Trust",
                "Regulatory Compliance",
                "Competitive Advantage",
                "Improved Client Confidence",
                "Enhanced Reputation",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve SOC Type 3 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              To achieve SOC Type 3 certification, organizations follow these
              steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Develop and Implement Security Controls — Establish security controls and processes that align with the Trust Services Criteria (security, availability, confidentiality, processing integrity, and privacy).",
                "2. Engage an Independent Auditor — Work with an independent third-party auditor to assess the effectiveness of your security controls and provide a transparent, objective report.",
                "3. Prepare Documentation and Evidence — Gather all necessary documentation and evidence to support the assessment of your security controls, including policies, procedures, and control performance data.",
                "4. Conduct the Audit and Issue the Report — The auditor will assess your organization’s control environment, evaluate performance against the Trust Services Criteria, and issue the SOC Type 3 report for public distribution.",
                "5. Monitor and Maintain Controls — Regularly monitor your security controls and implement improvements based on audit findings, feedback, and emerging risks.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for SOC Type 3 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert SOC Type 3 certification
              consultancy services, helping businesses implement robust security
              controls and undergo independent audits to demonstrate their
              commitment to security, privacy, and data protection.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "SOC Type 3 Readiness Assessment",
                "Control Implementation and Optimization",
                "Third-Party Audit Coordination",
                "Internal Audit and Pre-certification Support",
                "Continuous Monitoring and Risk Mitigation",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve SOC Type 3 certification, ensuring
              transparency, data protection, and regulatory compliance.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SOCType3_Details;
